import type { TranslationKey } from "@/lib/i18n";
import type { InboxNotification } from "@/lib/autar/notifications";

type Translate = (
  key: TranslationKey,
  params?: Record<string, string | number>,
) => string;

interface Rendered {
  title: string;
  body: string;
  href: string | null;
}

const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const path = (v: unknown) =>
  typeof v === "string" && /^\/(?![/\\])/.test(v) ? v : null;
const key = (k: string) => `notifications.${k}` as TranslationKey;

/**
 * The outcome is always in the title's words ("failed", "completed"), so it
 * never depends on colour or an icon alone.
 */
const plain =
  (stem: string, bodyKey = `${stem}.body`) =>
  (n: InboxNotification, t: Translate): Rendered => ({
    title: t(key(`${stem}.title`), { name: str(n.data.repositoryName) }),
    body: t(key(bodyKey)),
    href: path(n.data.link),
  });

export const workRenderers: Record<
  string,
  (n: InboxNotification, t: Translate) => Rendered
> = {
  "scan.completed": plain("scan.completed"),
  "scan.failed": plain("scan.failed"),
  "scan.first_completed": (n, t) => {
    const { score, grade } = n.data;
    const scored = typeof score === "number" && str(grade) !== "";
    return {
      title: t(key("scan.first.title"), { name: str(n.data.repositoryName) }),
      body: scored
        ? t(key("scan.first.bodyScore"), {
            score: Math.round(score),
            grade: str(grade),
          })
        : t(key("scan.first.body")),
      href: path(n.data.link),
    };
  },
  "maintenance_task.completed": (n, t) =>
    plain(
      "task.completed",
      str(n.data.prUrl) ? "task.completed.bodyPr" : "task.completed.body",
    )(n, t),
  "maintenance_task.failed": plain("task.failed"),
};
